import { useEffect, useRef, useState } from 'react';
import { getCellForStep, isSafeCell } from './ludoBoard.js';
import { playMovingToken, playSafePlace, unlockLudoAudio } from './ludoSounds.js';

export const TOKEN_STEP_MS = 220;

function cloneTokens(tokens) {
  const copy = {};
  for (const color of Object.keys(tokens || {})) {
    copy[color] = (tokens[color] || []).map((t) => ({ ...t }));
  }
  return copy;
}

function hasTokens(tokens) {
  return tokens && Object.keys(tokens).length > 0;
}

function isStepOnSafeCell(color, step) {
  const cell = getCellForStep(color, step);
  if (!cell) return false;
  return isSafeCell(cell[0], cell[1]);
}

function playHopSound(color, step, isLastHop) {
  unlockLudoAudio();
  if (isLastHop && isStepOnSafeCell(color, step)) {
    playSafePlace();
    return;
  }
  playMovingToken();
}

/** Each new cell the token enters after a roll (excludes the starting cell). */
export function getStepPath(fromStep, toStep) {
  if (toStep <= 0 || toStep <= fromStep) return [];
  const path = [];
  const start = fromStep === 0 ? 1 : fromStep + 1;
  for (let step = start; step <= toStep; step += 1) path.push(step);
  return path;
}

function findForwardMove(prev, next) {
  let best = null;

  for (const color of Object.keys(next)) {
    const prevList = prev[color] || [];
    const nextList = next[color] || [];

    nextList.forEach((nextToken, index) => {
      const prevToken = prevList[index];
      if (!prevToken) return;

      const prevStep = prevToken.step;
      const nextStep = nextToken.step;
      if (nextStep <= prevStep) return;

      const candidate = {
        tokenId: nextToken.id,
        color,
        fromStep: prevStep,
        toStep: nextStep,
      };

      if (!best || nextStep - prevStep > best.toStep - best.fromStep) {
        best = candidate;
      }
    });
  }

  return best;
}

function applyVisualSteps(tokens, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return tokens;

  const result = {};
  for (const color of Object.keys(tokens)) {
    result[color] = tokens[color].map((token) => {
      const visualStep = overrides[token.id];
      if (visualStep == null) return token;
      return { ...token, visualStep };
    });
  }
  return result;
}

export function useTokenMoveAnimation(tokens, resetKey = null) {
  const prevTokensRef = useRef(null);
  const readyRef = useRef(false);
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const animatingRef = useRef(false);
  const runAnimationRef = useRef(null);
  const [overrides, setOverrides] = useState({});
  const [animatingTokenId, setAnimatingTokenId] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  runAnimationRef.current = () => {
    const nextMove = queueRef.current.shift();
    if (!nextMove) {
      animatingRef.current = false;
      setIsAnimating(false);
      setAnimatingTokenId(null);
      return;
    }

    const path = getStepPath(nextMove.fromStep, nextMove.toStep);
    if (path.length === 0) {
      runAnimationRef.current();
      return;
    }

    animatingRef.current = true;
    setIsAnimating(true);
    setAnimatingTokenId(nextMove.tokenId);

    let index = 0;
    const step = () => {
      const currentStep = path[index];
      const isLastHop = index === path.length - 1;
      playHopSound(nextMove.color, currentStep, isLastHop);
      setOverrides({ [nextMove.tokenId]: currentStep });
      index += 1;

      if (index < path.length) {
        timerRef.current = window.setTimeout(step, TOKEN_STEP_MS);
        return;
      }

      timerRef.current = window.setTimeout(() => {
        setOverrides({});
        setAnimatingTokenId(null);
        runAnimationRef.current();
      }, TOKEN_STEP_MS);
    };

    step();
  };

  useEffect(() => {
    readyRef.current = false;
    prevTokensRef.current = null;
    queueRef.current = [];
    animatingRef.current = false;
    setOverrides({});
    setAnimatingTokenId(null);
    setIsAnimating(false);
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, [resetKey]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!hasTokens(tokens)) return;

    const prev = prevTokensRef.current;
    prevTokensRef.current = cloneTokens(tokens);

    if (!readyRef.current) {
      readyRef.current = true;
      return;
    }
    if (!prev) return;

    const move = findForwardMove(prev, tokens);
    if (!move) return;

    queueRef.current.push(move);
    if (!animatingRef.current) {
      runAnimationRef.current();
    }
  }, [tokens]);

  return {
    displayTokens: applyVisualSteps(tokens, overrides),
    isAnimating,
    animatingTokenId,
  };
}
